import React, { useState, useEffect, useRef } from 'react';
import {
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { auth } from '../../components/firebaseConfig';
import toast from 'react-hot-toast';
import logo from '../../img/peron_logo_black.png';
import abb from '../../img/abb.png';

function Login() {
  const [isMfaRequired, setIsMfaRequired] = useState(false);
  const [resolver, setResolver] = useState(null);
  const [verificationId, setVerificationId] = useState('');
  const [loading, setLoading] = useState(false);
  const [inputValues, setInputValues] = useState(['', '', '', '', '', '']); // 6 kutucuk için state
  const [timeLeft, setTimeLeft] = useState(120); // 2 dakika bekleme süresi
  const [canResend, setCanResend] = useState(false); // Kod tekrar gönderim kontrolü
  const inputsRef = useRef([]); // Kutucuklara referans

  useEffect(() => {
    let timer;
    if (timeLeft > 0 && isMfaRequired) {
      timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    } else if (timeLeft === 0) {
      setCanResend(true); // Süre dolunca kod tekrar gönderilebilir
    }
    return () => clearTimeout(timer);
  }, [timeLeft, isMfaRequired]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    const password = e.target.password.value;
  
    setLoading(true); // Loader'ı başlat
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
  
      const idTokenResult = await user.getIdTokenResult();
      const claims = idTokenResult.claims;
  
      if (claims.is_admin) {
        toast.success("Giriş Başarılı! Admin olarak oturum açtınız.");
        setLoading(false); // Giriş başarılıysa loader'ı durdur
      } else if (claims.is_editor) {
        toast.success("Giriş Başarılı! Editor olarak oturum açtınız.");
        setLoading(false); // Giriş başarılıysa loader'ı durdur
      } else {
        await auth.signOut();
        toast.error("Giriş yetkiniz bulunmamaktadır.");
        setLoading(false); // Yetki yoksa loader'ı durdur
      }
    } catch (error) {
      if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, error);
        setResolver(resolver);
  
        if (resolver.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
          const recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
            size: 'invisible',
          });
          const phoneAuthProvider = new PhoneAuthProvider(auth);
          const phoneInfoOptions = { multiFactorHint: resolver.hints[0], session: resolver.session };
  
          phoneAuthProvider
            .verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
            .then((verificationId) => {
              setVerificationId(verificationId);
              setIsMfaRequired(true);
              setTimeLeft(120); // Süreyi sıfırla
              setCanResend(false);
              toast.success("SMS doğrulama kodu gönderildi.");
            })
            .catch(() => {
              toast.error("SMS gönderiminde hata oluştu.");
            })
            .finally(() => {
              setLoading(false); // SMS ekranı tamamen yüklendikten sonra loader'ı durdur
            });
        } else {
          toast.error("Desteklenmeyen doğrulama yöntemi.");
          setLoading(false);
        }
      } else {
        let errorMessage;
        switch (error.code) {
          case 'auth/invalid-email':
            errorMessage = "Geçersiz e-posta adresi.";
            break;
          case 'auth/user-disabled':
            errorMessage = "Bu hesap devre dışı bırakılmış.";
            break;
          case 'auth/user-not-found':
            errorMessage = "Bu e-posta adresine ait bir kullanıcı bulunamadı.";
            break;
          case 'auth/wrong-password':
            errorMessage = "Hatalı şifre. Lütfen tekrar deneyin.";
            break;
          case 'auth/too-many-requests':
            errorMessage = "Çok fazla giriş denemesi yapıldı.";
            break;
          case 'auth/network-request-failed':
            errorMessage = "Ağ bağlantısı başarısız oldu.";
            break;
          default:
            errorMessage = "Bilinmeyen bir hata oluştu.";
        }
        toast.error("Giriş Hatası: " + errorMessage);
        setLoading(false); // Hata durumunda loader'ı durdur
      }
    }
  };
  

  const handleVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    const verificationCode = inputValues.join(''); // 6 kutudaki kodları birleştir

    try {
      const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
      await resolver.resolveSignIn(multiFactorAssertion);
      toast.success("Giriş Başarılı! SMS doğrulandı.");
      setIsMfaRequired(false);
    } catch (error) {
      toast.error("Doğrulama hatalı. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = () => {
    setTimeLeft(120); // Yeniden başlat
    setCanResend(false);
    toast.success("Yeni SMS doğrulama kodu gönderildi.");
  };

  const handleInputChange = (e, index) => {
    const value = e.target.value;
    if (!/^[0-9]$/.test(value) && value !== '') return; // Sadece rakam girilebilir

    const newInputValues = [...inputValues];
    newInputValues[index] = value;
    setInputValues(newInputValues);

    if (value && index < 5) {
      inputsRef.current[index + 1].focus(); // Bir sonraki kutuya geçiş
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !inputValues[index] && index > 0) {
      inputsRef.current[index - 1].focus(); // Önceki kutuya geçiş
    }
  };

  return (
    <div className=" h-screen overflow-hidden flex flex-col justify-between" style={{
      backgroundColor:"#F8F7F7"
    }}>
      <main className="relative isolate px-6 pt-0 lg:px-8 flex-grow">
        <div className="flex min-h-screen justify-center flex-col py-12 sm:px-6 lg:px-8">
          
          <div className="sm:mx-auto sm:w-full sm:max-w-md flex justify-center">
            <img src={logo} alt="Peron Logo" className="h-20 w-auto" />
          </div>

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
              </div>
            )}
            <div className="bg-white py-8 px-4 shadow-xl sm:rounded-lg sm:px-10 border-2 border-black-100 border-b-indigo-200">
              {!isMfaRequired ? (
                <form className="space-y-6" onSubmit={handleLogin}>
                  <div id="recaptcha-container"></div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      E-posta adresi
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-300 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Şifre
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-300 sm:text-sm"
                    />
                  </div>
                  <button
                    type="submit"
                    className="bg-gray-900 flex w-full justify-center rounded-md py-2 px-4 text-sm text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-300 sm:text-sm"
                  >
                    Giriş Yap
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerification}>
                  <div className="mb-4">
                    <h2 className="text-lg font-medium text-center">SMS Doğrulama Kodu</h2>
                    <p className="text-sm text-gray-500 text-center">
                      Kod süresi {timeLeft} saniye geçerlidir, yeniden kod gönderebilmek için; bu sürenin bitmesini bekleyin!
                    </p>
                    {canResend && (
                      <button
                        type="button"
                        className="text-blue-500 underline text-sm mt-2"
                        onClick={handleResendCode}
                      >
                        Yeniden kod gönder
                      </button>
                    )}
                  </div>
                  <div className="flex justify-center space-x-2">
                    {inputValues.map((value, index) => (
                      <input
                        key={index}
                        ref={(el) => (inputsRef.current[index] = el)}
                        type="text"
                        value={value}
                        maxLength="1"
                        onChange={(e) => handleInputChange(e, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        className="w-10 h-10 text-center border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-lg"
                      />
                    ))}
                  </div>
                  <button
                    type="submit"
                    className="mt-4 bg-indigo-600 w-full rounded-md py-2 px-4 text-sm text-white shadow-sm hover:bg-indigo-500"
                  >
                    Doğrula
                  </button>
                </form>
              )}
            </div>
          </div>
          <footer className="sm:mx-auto sm:w-full sm:max-w-md flex flex-col items-center justify-center py-4">
            <div className="flex justify-center relative top-2">
              <img src={abb} alt="Abb" className="h-8 w-auto" />
            </div>
            <div className="mt-2 text-center relative top-10 flex flex-col space-y-2">
              <div className="font-MahameruLight text-xs text-gray-700">
                <a href="https://www.peronmedya.com">
                  Uygulama <label className="text-gray-900 underline underline-offset-1 cursor-pointer">Peron Medya</label> iştirakidir.
                </a>
              </div>
              <div className="flex space-x-4 font-MahameruLight text-xs text-gray-600">
                <label>
                  Tüm hakları saklıdır!
                </label>
                <div className="font-MahameruLight text-xs text-gray-700 flex items-center space-x-1">
                  <a href="https://www.peronmedya.com/buyuksehirgizlilik" className="flex items-center">
                    <span className="pr-1">Gizlilik politikası</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="h-4 w-4 relative"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
}

export default Login;
